import { LoadingButton } from "@mui/lab";
import {
	Alert,
	AlertTitle,
	Autocomplete,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers-pro";
import moment from "moment-timezone";
import { Table } from "components/table/shift/Table";
import { GlobalContext } from "GlobalContext";
import { editingShift, gettingShiftById } from "utils/Schedule";

const EditShift = ({ open, setOpen, shiftId, employees, reloading }) => {
	const { t } = useTranslation();

	const { handlerAlertLocal } = useContext(GlobalContext);

	const [selectedEmployees, setSelectedEmployees] = useState([]);
	const [selectedDay, setSelectedDay] = useState(null);
	const [rows, setRows] = useState([]);

	const [loadingGettingShift, setLoadingGettingShift] = useState(false);
	const [loadingEditingShift, setLoadingEditingShift] = useState(false);

	const closing = () => {
		setOpen(false);
		setSelectedEmployees([]);
		setSelectedDay(null);
		setRows([]);
	};

	const gettingSegments = (segments) => {
		let repeat = true;

		while (repeat) {
			repeat = false;

			let previous = null;

			for (let segment of segments) {
				if (previous) {
					if (previous.isAfter(segment)) {
						segment.add(1, "days");
						repeat = true;
					}
				}

				previous = segment;
			}
		}

		return segments;
	};

	useEffect(() => {
		if (open) {
			const em = filterEmployees();
			gettingShiftById(
				setLoadingGettingShift,
				shiftId,
				em,
				setSelectedEmployees,
				setSelectedDay,
				setRows
			);
			//setSelectedEmployees([])
			//setSelectedDay()
		}
		// eslint-disable-next-line
	}, [open]);

	const filterEmployees = useCallback(() => {
		const uniqueById = [];
		const seenIds = new Set();

		for (const employee of employees) {
			if (!seenIds.has(employee.id)) {
				seenIds.add(employee.id);
				uniqueById.push(employee);
			}
		}

		return uniqueById;
	}, [employees]);

	return (
		<>
			<Dialog
				fullWidth
				open={open}
				onClose={() => {
					closing();
				}}
			>
				<DialogTitle
					sx={{
						background: (theme) => theme.palette.background.default,
					}}
				>
					<Typography variant="body3">
						{t("schedule.table.modals.edit.title")}
					</Typography>
				</DialogTitle>

				{employees
					.filter((e) => selectedEmployees.includes(e.full_name))
					.some((e) =>
						moment(e?.pay_period_end, "YYYY-MM-DD").isSameOrAfter(
							selectedDay
						)
					) && ( //names.includes(period.full_name.slice(0, -15))).some(period => moment(period?.end_date, 'YYYY-MM-DD').isSameOrAfter(range[0])) &&
						<DialogContent
							sx={{
								backgroundColor: (theme) =>
									theme.palette.background.default,
								borderBottom: "none",
								height: "auto",
							}}
							dividers
						>
							<Grid container spacing={2}>
								<Grid size={12}>
									<Alert
										severity="warning"
										sx={{
											width: "100%",
											opacity: 1,
										}}
										variant="filled"
									>
										<AlertTitle>
											{t(
												"schedule.table.modals.edit.alerts.period_is_closed.title"
											)}
										</AlertTitle>
										{t(
											"schedule.table.modals.edit.alerts.period_is_closed.message"
										)}
									</Alert>
								</Grid>
							</Grid>
						</DialogContent>
					)}

				<DialogContent
					dividers
					sx={{
						background: (theme) => theme.palette.background.default,
					}}
				>
					<Grid container spacing={2}>
						<Grid size={12}>
							<Autocomplete
								multiple
								limitTags={5}
								options={useMemo(
									() =>
										filterEmployees().map(
											(employee) => employee?.full_name
										),
									[filterEmployees]
								)}
								value={selectedEmployees}
								onChange={(event, newValue) => {
									setSelectedEmployees(newValue);
									console.log("newValue", newValue);
								}}
								renderOption={(props, option, { selected }) => (
									<li key={props?.id} {...props}>
										<Checkbox
											icon={
												<CheckBoxOutlineBlankIcon fontSize="small" />
											}
											checkedIcon={
												<CheckBoxIcon fontSize="small" />
											}
											style={{ marginRight: 8 }}
											checked={selected}
										/>
										{option}
									</li>
								)}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip
											key={index}
											label={option}
											color={
												moment(
													employees.find(
														(p) =>
															p?.document_employee &&
															p?.full_name ===
															option
													)?.pay_period_end,
													"YYYY-MM-DD"
												).isSameOrAfter(selectedDay)
													? "warning"
													: undefined
											} // Utiliza el color personalizado
											{...getTagProps({ index })}
										/>
									))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t(
											"schedule.table.modals.add.employees"
										)}
									/>
								)}
								fullWidth
								disabled={true}
							/>
						</Grid>

						<Grid size={6}>
							<DatePicker
								label={t("schedule.table.modals.add.days")}
								value={selectedDay}
								format="DD-MM-YYYY"
								onChange={(nv) => setSelectedDay(nv)}
								sx={{
									border: "1px solid rgba(0,0,0,0.23)",
									borderRadius: 1,
									width: "100%",
								}}
							/>
						</Grid>

						<Grid size={12}>
							<Table
								selectedDay={selectedDay}
								rows={rows}
								setRows={setRows}
								loading={loadingGettingShift}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions
					sx={{
						background: (theme) => theme.palette.background.default,
					}}
				>

					<LoadingButton
						sx={{ mr: 2.25 }}
						variant="contained"
						disableElevation
						onClick={() => {
							gettingSegments(
								rows.flatMap((row) => [
									row.start_ts,
									row.end_ts,
								])
							).map((segment) =>
								segment.format("YYYY-MM-DD HH:mm")
							);

							const end = rows[rows.length - 1]?.end_ts;
							const start = rows[0]?.start_ts;

							const duration = moment.duration(
								Math.abs(start.diff(end))
							);

							const hours = Math.floor(duration.asHours());
							const minutes = duration.minutes();
							const seconds = duration.seconds();

							editingShift(
								setLoadingEditingShift,
								shiftId,
								employees.find(
									(employee) =>
										selectedEmployees[0] ===
										employee.full_name
								)?.id,
								selectedDay.clone().format("YYYY-MM-DD"),
								start.utc().format("HH:mm:ss"),
								end.utc().format("HH:mm:ss"),
								`${String(hours).padStart(2, "0")}:${String(
									minutes
								).padStart(2, "0")}:${String(seconds).padStart(
									2,
									"0"
								)}`,
								moment.tz.guess(),
								rows,
								closing,
								reloading,
								handlerAlertLocal
							);
						}}
						loading={loadingEditingShift}
						disabled={
							rows.length === 0 ||
							selectedEmployees.length === 0 ||
							selectedDay === null ||
							rows.some(
								(row) =>
									row.start_ts === null || row.end_ts === null
							)
						}
					>
						{t("settings.paycodes.table.modals.edit.buttons.save")}
					</LoadingButton>

					<Button
						variant="contained"
						disableElevation
						color="error"
						onClick={() => {
							closing();
						}}
					>
						{t(
							"settings.paycodes.table.modals.edit.buttons.cancel"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export { EditShift };
